import { useCallback, useEffect } from "react";
import { useAccessibilityTags } from "common/hooks/business/home/useAccessibilityTags";
import { useAccessibilityTagsHomePage } from "common/hooks/business/home/useAccessibilityTagsHomePage";
import { useAuth, useSavedFilters, useFilters, useI18N } from "common/hooks";
import { useAtom } from "jotai";
import { savedFiltersAppliedAtom } from "@/store/atoms";
import Switch from "@/components/common/Switch";
import AnalyticsService from "@/services/analytics";
import AccessibilityCollapse from "./AccessibilityCollapse";

/**
 * Context-aware accessibility features component that works on both home and search pages.
 * This component automatically detects which page it's on and uses the appropriate data source:
 * - Search page: Uses accessibilityTagsAtom (from homePinsAtom/venueSearchParamsAtom)
 * - Home page: Uses desktopHomePageAccessibilityTagsAtom (from desktopHomePagePinsAtomFamily)
 * 
 * This fixes the issue where opening search bar immediately after page load
 * would show no accessibility filters.
 */
function AccessibilityFeaturesContextAware() {
  const i18n = useI18N();
  const { isLoggedIn } = useAuth();
  const { savedFilterIds, loadSavedFilters } = useSavedFilters();
  const { setFiltersState } = useFilters();
  const [isSavedFiltersApplied, setIsSavedFiltersApplied] = useAtom(
    savedFiltersAppliedAtom
  );

  // Get data from both sources
  const searchPageData = useAccessibilityTags();
  const homePageData = useAccessibilityTagsHomePage();

  // Intelligently choose which data to use:
  // Prefer search page data if it has content, otherwise fall back to home page data
  // This ensures the component works on both pages without needing explicit context
  const { accessibilityTags, preparedData } = 
    searchPageData.accessibilityTags && searchPageData.accessibilityTags.length > 0
      ? searchPageData
      : homePageData;

  // Load saved filters when component mounts
  useEffect(() => {
    if (isLoggedIn) {
      loadSavedFilters();
    }
  }, [isLoggedIn, loadSavedFilters]);

  // Apply saved filters function
  const applySavedFilters = useCallback(
    (apply: boolean) => {
      setIsSavedFiltersApplied(apply);

      if (savedFilterIds) {
        if (apply) {
          AnalyticsService.trackGAEventHandler("filtersAppliedSavedFiltersOn");
          AnalyticsService.trackMx("filtersAppliedSavedFiltersOn", {});

          // Add saved filter IDs to accessibility options
          setFiltersState((prev) => {
            const newOptions = [...prev.accessibilityOptions];
            savedFilterIds.forEach((id: number) => {
              if (!newOptions.includes(id)) {
                newOptions.push(id);
              }
            });
            return {
              ...prev,
              accessibilityOptions: newOptions,
            };
          });
        } else {
          AnalyticsService.trackGAEventHandler("filtersAppliedSavedFiltersOff");
          AnalyticsService.trackMx("filtersAppliedSavedFiltersOff", {});

          // Remove only saved filter IDs from accessibility options
          setFiltersState((prev) => ({
            ...prev,
            accessibilityOptions: prev.accessibilityOptions.filter(
              (o) => !savedFilterIds.includes(o)
            ),
          }));
        }
      }
    },
    [savedFilterIds, setFiltersState, setIsSavedFiltersApplied]
  );

  // Don't render if no accessibility tags data is available
  // Check for both null/undefined AND empty array
  if (!accessibilityTags || accessibilityTags.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {/* Saved Filters Toggle */}
      {isLoggedIn && (
        <div className="px-4 pt-2 pb-2">
          <div className="flex items-center gap-3 py-3 bg-bg-brand-ghost-default">
            <Switch
              isOn={isSavedFiltersApplied}
              onChange={(value) => {
                applySavedFilters(value);
              }}
              aria-label={
                isSavedFiltersApplied
                  ? `${i18n.get("applySavedAccessibilityFilters")} selected`
                  : `${i18n.get("applySavedAccessibilityFilters")}`
              }
            />
            <div className="text-primary-900 dark:text-primary-150 paragraph-medium-regular">
              {i18n.get("applySavedAccessibilityFilters")}
            </div>
          </div>
        </div>
      )}

      {/* Category Accordions */}
      {preparedData.map((option) => {
        if (option.accessibility_tags.length <= 0) return null;
        return (
          <AccessibilityCollapse
            label={option.name}
            key={option.name}
            tagsToShow={option.accessibility_tags}
          />
        );
      })}
    </div>
  );
}

export default AccessibilityFeaturesContextAware;
